import React, { useCallback, useEffect, useState } from 'react';
import { Button, Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import { format_datetime_dd_mmm_yy as fmt } from '../../../utils/format_date_dd_mmm_yy';

/**
 * Threaded comments on a Task. Any rep can post; the AI can also author
 * comments (authorType='ai'), rendered with its name. Distinct from the
 * status/field audit history — this is human-readable discussion.
 */
// Friendly author label: AI keeps its name; a raw email is turned into a
// readable name (suman.kumari@infurnia.com → "Suman Kumari") so comments don't
// show raw email ids.
const pretty_author = (c) => {
    if (c.authorType === 'ai') return c.authorName || 'AI';
    const n = (c.authorName || '').trim();
    if (n.includes('@')) {
        const local = n.split('@')[0];
        const name = local.split(/[._-]/).filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return name || n;
    }
    return n || 'user';
};

const TaskComments = ({ store_id, actionable_id }) => {
    const [comments, set_comments] = useState([]);
    const [draft, set_draft] = useState('');
    const [busy, set_busy] = useState(false);
    const [err, set_err] = useState(null);

    const base = `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${actionable_id}/comments`;

    const load = useCallback(async () => {
        try {
            const resp = await cs_analysis_fetch({ path: base });
            set_comments(Array.isArray(resp) ? resp : []);
        } catch (e) { set_err(e.message || String(e)); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store_id, actionable_id]);

    useEffect(() => { load(); }, [load]);

    const post = async () => {
        if (!draft.trim() || busy) return;
        set_busy(true); set_err(null);
        try {
            await cs_analysis_fetch({ path: base, method: 'POST', body: { body: draft.trim() } });
            set_draft('');
            await load();
        } catch (e) { set_err(e.message || String(e)); } finally { set_busy(false); }
    };

    return (
        <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Comments</div>
            {comments.length === 0 ? <div style={{ fontSize: 12, color: '#bdc3c7', marginBottom: 4 }}>No comments yet.</div> : null}
            {comments.map((c) => (
                <div key={c.id} style={{ fontSize: 12, marginBottom: 6, paddingBottom: 6, borderBottom: '1px solid #f0f2f5' }}>
                    <div>
                        <span style={{ fontWeight: 600, color: c.authorType === 'ai' ? '#8e44ad' : '#2c3e50' }}>
                            {c.authorType === 'ai' ? '🤖 ' : ''}{pretty_author(c)}
                        </span>
                        <span style={{ color: '#b2bec3', marginLeft: 6, fontSize: 11 }}>{fmt(c.createdAt)}</span>
                    </div>
                    <div style={{ color: '#57606f', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <Input
                    type='textarea' rows={1} bsSize='sm' placeholder='Add a comment…' value={draft}
                    onChange={(e) => set_draft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); post(); } }}
                    style={{ fontSize: 12 }}
                />
                <Button size='sm' color='primary' disabled={busy || !draft.trim()} onClick={post} style={{ flexShrink: 0 }}>Post</Button>
            </div>
            {err ? <div style={{ color: '#c0392b', fontSize: 11, marginTop: 4 }}>{err}</div> : null}
        </div>
    );
};

export default TaskComments;
